import React, { useState, useEffect } from 'react';
import { Order, OrderItem, Location, OrderStatus, Customer, Product, Category, User } from '../types';
import { LOCATIONS, STATUSES } from '../constants';
import { Save, Trash2, Plus, X, ChevronLeft, Check, Minus, Delete, ShoppingBag, Utensils, Grid, Calendar } from 'lucide-react';
import { generateEasternTimestamp } from '../utils';

interface OrderFormProps {
  existingOrder?: Order | null;
  onSave: (order: Order) => void;
  onCancel: () => void;
  customers: Customer[];
  products: Product[];
  categories: Category[];
  user: User;
  loading?: boolean;
}

// --- Sub-component: Numpad ---
const Numpad = ({ value, onChange, onClear }: { value: string, onChange: (val: string) => void, onClear: () => void }) => {
    const handlePress = (num: string) => {
        onChange(value + num);
    };

    const handleBackspace = () => {
        onChange(value.slice(0, -1));
    };

    return (
        <div className="bg-gray-100 p-2 rounded-lg max-w-xs mx-auto">
            <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button 
                        key={num}
                        type="button"
                        onClick={() => handlePress(num.toString())}
                        className="h-12 bg-white rounded shadow-sm text-xl font-semibold text-gray-700 active:bg-gray-200"
                    >
                        {num}
                    </button>
                ))}
                <button type="button" onClick={onClear} className="h-12 bg-red-100 rounded shadow-sm text-sm font-semibold text-red-700 active:bg-red-200">CLR</button>
                <button type="button" onClick={() => handlePress('0')} className="h-12 bg-white rounded shadow-sm text-xl font-semibold text-gray-700 active:bg-gray-200">0</button>
                <button type="button" onClick={handleBackspace} className="h-12 bg-gray-200 rounded shadow-sm flex items-center justify-center active:bg-gray-300">
                    <Delete className="w-5 h-5 text-gray-600" />
                </button>
            </div>
        </div>
    );
};

const OrderForm: React.FC<OrderFormProps> = ({ existingOrder, onSave, onCancel, customers, products, categories, user, loading = false }) => {
  // Form State
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [location, setLocation] = useState<Location>('Cranberry');
  const [status, setStatus] = useState<OrderStatus>('Pending');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<OrderItem[]>([]);

  // Format phone number to ###-###-####
  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format as ###-###-####
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }

    // Return original if not 10 digits
    return phoneNumber;
  };

  // Display formatted phone but store raw digits
  const handleFormattedPhoneChange = (value: string) => {
    // Store raw digits
    const rawDigits = value.replace(/\D/g, '');
    setPhone(rawDigits);

    if (rawDigits.length > 2) {
      const matches = customers.filter(c => c.phone.includes(rawDigits));
      setPhoneSuggestions(matches.slice(0, 5));
    } else {
      setPhoneSuggestions([]);
    }
  };

  // UI State
  const [showNumpad, setShowNumpad] = useState(false);
  const [phoneSuggestions, setPhoneSuggestions] = useState<Customer[]>([]);

  // POS Wizard State
  const [isItemWizardOpen, setItemWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<'CATEGORY' | 'PRODUCT' | 'DETAILS'>('CATEGORY');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  // Temporary item details in wizard
  const [wizQty, setWizQty] = useState(1);
  const [wizWeightMin, setWizWeightMin] = useState('');
  const [wizWeightMax, setWizWeightMax] = useState('');
  const [wizNotes, setWizNotes] = useState('');

  useEffect(() => {
    if (existingOrder) {
      setPhone(existingOrder.phone);
      setFirstName(existingOrder.firstName);
      setLastName(existingOrder.lastName);

      // Ensure pickup date is properly formatted as YYYY-MM-DD
      if (existingOrder.pickupDate) {
        // Try to parse the date and format it correctly
        try {
          const dateObj = new Date(existingOrder.pickupDate);
          const formattedDate = dateObj.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          setPickupDate(formattedDate);
        } catch (e) {
          console.error("Error formatting pickup date:", e);
          setPickupDate(existingOrder.pickupDate); // Fallback to original value
        }
      } else {
        // If no pickup date, set to today
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setPickupDate(formattedDate);
      }

      setLocation(existingOrder.location);
      setStatus(existingOrder.status);
      setNotes(existingOrder.notes);
      setItems(existingOrder.items);
    } else {
      // Set default date to today for new orders
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      setPickupDate(formattedDate);
    }
  }, [existingOrder]);

  // This function is replaced by handleFormattedPhoneChange
  const handlePhoneChange = handleFormattedPhoneChange;

  const selectCustomer = (customer: Customer) => {
    // Store raw digits only
    setPhone(customer.phone.replace(/\D/g, ''));
    setFirstName(customer.firstName);
    setLastName(customer.lastName);
    setPhoneSuggestions([]);
    if (!showNumpad) {
        // Only close if we aren't explicitly in keypad mode
    }
  };

  // --- Wizard Logic ---
  const openItemWizard = () => {
      setWizardStep('CATEGORY');
      setSelectedCategory(null);
      setSelectedProduct(null);
      setWizQty(1);
      setWizWeightMin('');
      setWizWeightMax('');
      setWizNotes('');
      setItemWizardOpen(true);
  };

  const handleCategorySelect = (catName: string) => {
      setSelectedCategory(catName);
      setWizardStep('PRODUCT');
  };

  const handleProductSelect = (product: Product) => {
      setSelectedProduct(product);
      setWizardStep('DETAILS');
  };

  const handleWizardComplete = () => {
      if (!selectedProduct) return;

      const newItem: OrderItem = {
          id: crypto.randomUUID(),
          productId: selectedProduct.id,
          name: selectedProduct.name,
          price: selectedProduct.price,
          quantity: wizQty,
          weightMin: wizWeightMin,
          weightMax: wizWeightMax,
          isDone: false,
          notes: wizNotes
      };

      setItems([...items, newItem]);
      setItemWizardOpen(false);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const toggleItemDone = (index: number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], isDone: !newItems[index].isDone };
    setItems(newItems);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !phone || !pickupDate) {
        alert("Please fill in required customer and date fields.");
        return;
    }

    // Use Eastern timezone for all timestamps
    const now = generateEasternTimestamp();
    let history = existingOrder?.statusHistory || [];

    if (!existingOrder) {
        history = [{ status, timestamp: now, updatedBy: user.name }];
    } else if (existingOrder.status !== status) {
        history = [...history, { status, timestamp: now, updatedBy: user.name }];
    }

    const orderPayload: Order = {
      id: existingOrder ? existingOrder.id : crypto.randomUUID(),
      firstName,
      lastName,
      customerName: `${firstName} ${lastName}`,
      phone,
      pickupDate,
      location,
      status,
      notes,
      items,
      createdAt: existingOrder ? existingOrder.createdAt : now,
      takenBy: existingOrder ? existingOrder.takenBy : user.name,
      lastUpdated: now,
      statusHistory: history
    };
    onSave(orderPayload);
  };

  return (
    <>
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden relative">
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-700 font-medium">Loading customer data...</p>
          </div>
        </div>
      )}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center sticky top-0 z-10">
        <h2 className="text-xl font-bold text-gray-800">{existingOrder ? 'Edit Order' : 'New Order'}</h2>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full">
            <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8 pb-24">
        {/* Section 1: Customer & Logistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone Input with Numpad Toggle */}
            <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <div className="flex">
                    <input 
                        type="text" 
                        value={formatPhoneNumber(phone)}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        onFocus={() => setShowNumpad(true)}
                        className="w-full p-3 border border-gray-300 rounded-l focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-gray-900 bg-white text-lg"
                        placeholder="Type or use keypad..."
                        required
                    />
                    <button 
                        type="button" 
                        onClick={() => setShowNumpad(!showNumpad)}
                        className={`px-4 bg-gray-100 border border-l-0 border-gray-300 rounded-r text-gray-600 ${showNumpad ? 'bg-red-100 text-red-600' : ''}`}
                    >
                        <Grid className="w-6 h-6" />
                    </button>
                </div>

                {/* Autocomplete Results */}
                {phoneSuggestions.length > 0 && (
                    <ul className={`bg-white border border-gray-200 rounded shadow-xl mt-1 max-h-48 overflow-y-auto ${showNumpad ? 'relative w-full z-0 mb-2 border-t-0 rounded-t-none' : 'absolute z-20 w-full'}`}>
                        {phoneSuggestions.map(c => (
                            <li 
                                key={c.id} 
                                onClick={() => selectCustomer(c)}
                                className="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-0"
                            >
                                <span className="font-bold text-base block">{formatPhoneNumber(c.phone)}</span>
                                {c.firstName} {c.lastName}
                            </li>
                        ))}
                    </ul>
                )}

                {/* Inline Numpad */}
                {showNumpad && (
                    <div className="mt-2 p-2 bg-white border border-gray-200 rounded-lg shadow-sm animate-in slide-in-from-top-2 duration-200">
                        <Numpad value={phone} onChange={handlePhoneChange} onClear={() => handlePhoneChange('')} />
                        <div className="text-center mt-2">
                            <button type="button" onClick={() => setShowNumpad(false)} className="text-xs text-red-600 font-medium p-2">Close Keypad</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input 
                        type="text" 
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none text-gray-900 bg-white text-lg"
                        required
                    />
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input 
                        type="text" 
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none text-gray-900 bg-white text-lg"
                        required
                    />
                 </div>
            </div>

            <div className="relative">
                 <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date *</label>
                 <div className="relative">
                    <input 
                        type="date" 
                        value={pickupDate}
                        onChange={e => setPickupDate(e.target.value)}
                        onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none text-gray-900 bg-white text-lg appearance-none"
                        required
                    />
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-6 h-6" />
                 </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <select 
                        value={location}
                        onChange={e => setLocation(e.target.value as Location)}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none bg-white text-gray-900 text-base"
                    >
                        {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select 
                        value={status}
                        onChange={e => setStatus(e.target.value as OrderStatus)}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none bg-white text-gray-900 text-base"
                    >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                 </div>
            </div>

            <div className="md:col-span-2">
                 <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes</label>
                 <textarea 
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none text-gray-900 bg-white text-base"
                    rows={2}
                    placeholder="General instructions..."
                 />
            </div>
        </div>

        <hr className="border-gray-200" />

        {/* Section 2: Items */}
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Order Items
                </h3>
            </div>

            <div className="space-y-4 mb-6">
                {items.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-gray-500">
                        No items added yet. Tap below to start.
                    </div>
                )}
                {items.map((item, index) => (
                    <div 
                        key={item.id} 
                        className={`p-4 rounded-lg border shadow-sm relative flex justify-between items-center transition-colors ${item.isDone ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}
                    >
                        <div className="flex items-center flex-1">
                            {/* Toggle Done Button */}
                            <button 
                                type="button"
                                onClick={() => toggleItemDone(index)}
                                className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center mr-4 transition-colors ${item.isDone ? 'bg-green-500 border-green-600 text-white' : 'bg-white border-gray-300 text-gray-300 hover:border-gray-400'}`}
                            >
                                <Check className="w-5 h-5" />
                            </button>

                            <div>
                                <div className="flex items-center">
                                    <span className={`font-bold text-lg mr-2 ${item.isDone ? 'text-green-900' : 'text-gray-900'}`}>{item.quantity}x</span>
                                    <span className={`text-lg font-medium ${item.isDone ? 'text-green-800 line-through decoration-green-800/30' : 'text-gray-800'}`}>{item.name}</span>
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {item.weightMin && `Weight: ${item.weightMin} - ${item.weightMax} lbs`}
                                    {item.notes && <span className="block text-gray-600 italic">"{item.notes}"</span>}
                                </div>
                            </div>
                        </div>

                        <button 
                            type="button" 
                            onClick={() => handleRemoveItem(index)}
                            className="p-3 text-red-500 bg-red-50 rounded-full hover:bg-red-100 ml-2"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                ))}
            </div>

            <button 
                type="button" 
                onClick={openItemWizard}
                className="w-full py-4 bg-red-50 border-2 border-dashed border-red-300 text-red-700 rounded-xl font-bold text-lg flex items-center justify-center hover:bg-red-100 transition-colors"
            >
                <Plus className="w-6 h-6 mr-2" />
                Add Item
            </button>
        </div>

        {/* Floating Save Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg md:sticky md:bottom-0 z-20">
            <div className="max-w-4xl mx-auto flex gap-4">
                <button 
                    type="button" 
                    onClick={onCancel}
                    className="flex-1 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-lg"
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="flex-[2] py-3 bg-red-800 text-white rounded-xl hover:bg-red-900 font-bold text-lg flex items-center justify-center shadow-md"
                >
                    <Save className="w-5 h-5 mr-2" />
                    Save Order
                </button>
            </div>
        </div>
      </form>
    </div>

    {/* --- POS Item Wizard Modal --- */}
    {isItemWizardOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4">
            <div className="bg-white w-full md:max-w-2xl h-[90vh] md:h-auto md:max-h-[90vh] rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-300">

                {/* Wizard Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div className="flex items-center">
                        {wizardStep !== 'CATEGORY' && (
                            <button 
                                onClick={() => setWizardStep(prev => prev === 'DETAILS' ? 'PRODUCT' : 'CATEGORY')}
                                className="mr-3 p-1 rounded hover:bg-gray-200"
                            >
                                <ChevronLeft className="w-6 h-6 text-gray-600" />
                            </button>
                        )}
                        <h3 className="text-xl font-bold text-gray-800">
                            {wizardStep === 'CATEGORY' && 'Select Category'}
                            {wizardStep === 'PRODUCT' && selectedCategory}
                            {wizardStep === 'DETAILS' && selectedProduct?.name}
                        </h3>
                    </div>
                    <button onClick={() => setItemWizardOpen(false)} className="p-2 bg-gray-200 rounded-full hover:bg-gray-300">
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Wizard Content */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* Step 1: Categories */}
                    {wizardStep === 'CATEGORY' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {categories.map(cat => (
                                <button 
                                    key={cat.id} 
                                    onClick={() => handleCategorySelect(cat.name)}
                                    className="aspect-square bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-red-500 hover:bg-red-50 flex flex-col items-center justify-center p-4 transition-all"
                                >
                                    <Utensils className="w-10 h-10 text-red-800 mb-3" />
                                    <span className="font-bold text-gray-800 text-center">{cat.name}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Step 2: Products */}
                    {wizardStep === 'PRODUCT' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {products.filter(p => p.category === selectedCategory).map(prod => (
                                <button 
                                    key={prod.id} 
                                    onClick={() => handleProductSelect(prod)}
                                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-red-500 hover:bg-red-50 flex flex-col items-center justify-center p-6 transition-all min-h-[120px]"
                                >
                                    <span className="font-bold text-gray-800 text-center text-lg">{prod.name}</span>
                                </button>
                            ))}
                            {products.filter(p => p.category === selectedCategory).length === 0 && (
                                <div className="col-span-full text-center text-gray-500 py-10">
                                    No products found in this category.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Details */}
                    {wizardStep === 'DETAILS' && (
                        <div className="space-y-8 max-w-sm mx-auto">
                            {/* Quantity Stepper */}
                            <div className="text-center">
                                <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Quantity</label>
                                <div className="flex items-center justify-center gap-6">
                                    <button 
                                        type="button" 
                                        onClick={() => setWizQty(Math.max(1, wizQty - 1))}
                                        className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 active:bg-gray-300"
                                    >
                                        <Minus className="w-8 h-8" />
                                    </button>
                                    <span className="text-4xl font-bold text-gray-900 w-16 tabular-nums">{wizQty}</span>
                                    <button 
                                        type="button" 
                                        onClick={() => setWizQty(wizQty + 1)}
                                        className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center text-red-600 hover:bg-red-200 active:bg-red-300"
                                    >
                                        <Plus className="w-8 h-8" />
                                    </button>
                                </div>
                            </div>

                            {/* Weight */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                <label className="block text-sm font-medium text-gray-500 mb-3 text-center uppercase tracking-wide">Weight Range (Lbs)</label>
                                <div className="flex items-center gap-4">
                                    <input 
                                        type="number" 
                                        placeholder="Min" 
                                        value={wizWeightMin}
                                        onChange={(e) => setWizWeightMin(e.target.value)}
                                        className="w-full p-3 text-center text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-gray-900" 
                                    />
                                    <span className="text-gray-400 font-bold">-</span>
                                    <input 
                                        type="number" 
                                        placeholder="Max" 
                                        value={wizWeightMax}
                                        onChange={(e) => setWizWeightMax(e.target.value)}
                                        className="w-full p-3 text-center text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-gray-900" 
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Item Notes</label>
                                <textarea 
                                    value={wizNotes}
                                    onChange={(e) => setWizNotes(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-gray-900"
                                    placeholder="Special requests (e.g. cut thick)"
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Wizard Footer Actions */}
                {wizardStep === 'DETAILS' && (
                    <div className="p-4 border-t border-gray-200 bg-white">
                        <button 
                            type="button"
                            onClick={handleWizardComplete}
                            className="w-full py-4 bg-green-600 text-white rounded-xl font-bold text-xl flex items-center justify-center shadow-lg active:scale-[0.98] transition-transform"
                        >
                            <Check className="w-6 h-6 mr-2" />
                            Add to Order
                        </button>
                    </div>
                )}
            </div>
        </div>
    )}
    </>
  );
};

export default OrderForm;
